import { useEffect, useState, type ReactNode } from "react";
import { CLOSE_LINE } from "@/lib/discovery-call";
import { useLocation } from "react-router-dom";
import { Link } from "./Navigation";
import { FooterMark } from "./FooterMark";
import { ArrowUpRight, Menu, Phone } from "lucide-react";
import { VERTICALS } from "@/data/verticals";
import { COMPARISONS } from "@/data/comparisons";
import { HVAC_ROUNDUP_PATH } from "@/data/hvacRoundup";
import { ACQ_HUB_LINKS } from "@/data/acquisition/manifest";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import logo from "@/assets/logo-wordmark-full.webp";
import { SITE_IDENTITY } from "@/lib/site-identity";
import { openCookieSettings } from "@/lib/consent";
import "./rebuild.css";
import "./glass.css";
import "./finishing.css";
import "./inner-pages.css";
import "./industry-photos.css";
import { SOLUTIONS } from "./InnerSections";
import { BookingPopup } from "./BookingPopup";

export const PHONE = "tel:+18136948671";
export const PORTAL = "https://app.chlebholdings.com";
// 2026-09-07 , four links, and About is one of them. The bar used to carry a
// Solutions menu plus separate AI receptionist, CRM and Calculator links, and
// no About link at all, which is exactly why the operator could not find the
// About page. Solutions is now one page rather than a menu, the calculator
// stays where it lives (the home page), and the drawer carries the same four.
// 2026-09-25: Blog joins them (Dom: a link at the top straight to the stories).
export const links = [
  ["Solutions", "/solutions"],
  ["Industries", "/industries"],
  ["Pricing", "/pricing"],
  ["Blog", "/blog"],
  ["About", "/about"],
];
export const menuLinks = links;
// Which of the four the visitor is inside. Not NavLink's own path match: the
// seven trade pages are children of Industries but their routes are top level
// (/hvac, not /industries/hvac), so a path match leaves the whole bar dark on
// all seven. Somebody who follows Industries into HVAC should still be able
// to see where they are.
const VERTICAL_ROUTES = new Set(VERTICALS.map((v) => v.route));
const isCurrentSection = (pathname: string, path: string) =>
  pathname === path ||
  (path === "/industries" && VERTICAL_ROUTES.has(pathname)) ||
  (path === "/blog" && pathname.startsWith("/blog/"));
export function Header() {
  const [open, setOpen] = useState(false);
  // The bar is a quiet glass strip over the world. It only earns a hairline
  // once the page has moved under it: at rest the hero should read edge to
  // edge with nothing drawn across it.
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const read = () => setScrolled(window.scrollY > 8);
    read();
    addEventListener("scroll", read, { passive: true });
    return () => removeEventListener("scroll", read);
  }, []);
  return (
    <header className="rb-header" data-scrolled={scrolled ? "true" : "false"}>
      <Link className="rb-brand" to="/" aria-label="Chleb AI home">
        <img src={logo} alt="Chleb AI" width="320" height="132" />
      </Link>
      <nav aria-label="Main navigation" className="rb-desktop-nav">
        {links.map(([n, p]) => {
          const here = isCurrentSection(pathname, p);
          return (
            <Link
              key={p}
              to={p}
              className={here ? "active" : undefined}
              aria-current={here ? "page" : undefined}
            >
              {n}
            </Link>
          );
        })}
      </nav>
      <div className="rb-header-actions">
        <a className="rb-login" href={PHONE}>
          <Phone size={14} /> (813) 694-8671
        </a>
        <a
          className="rb-login"
          href={PORTAL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Client login <ArrowUpRight size={14} />
        </a>
        <Link className="rb-button rb-small" to="/book">
          Book a demo <ArrowUpRight size={16} />
        </Link>
        <a
          href={PHONE}
          className="rb-mobile-call"
          aria-label="Call (813) 694-8671"
        >
          <Phone size={20} />
        </a>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="rb-menu" aria-label="Open navigation">
              <Menu size={24} />
            </button>
          </SheetTrigger>
          <SheetContent className="rb-mobile-menu" aria-describedby={undefined}>
            <SheetTitle>Explore Chleb AI</SheetTitle>
            <nav aria-label="Mobile navigation">
              {menuLinks.map(([n, p]) => {
                const here = isCurrentSection(pathname, p);
                return (
                  <Link
                    key={p}
                    to={p}
                    className={here ? "active" : undefined}
                    aria-current={here ? "page" : undefined}
                  >
                    {n}
                    <ArrowUpRight size={20} />
                  </Link>
                );
              })}
              <div className="ip-mobile-solutions">
                <span>Solutions</span>
                {SOLUTIONS.map((s) => (
                  <a key={s.path} href={s.path}>
                    {s.name}
                    <ArrowUpRight size={18} />
                  </a>
                ))}
              </div>
              <Link to="/contact">Contact us</Link>
              <a href={PORTAL} target="_blank" rel="noopener noreferrer">
                Client login
              </a>
            </nav>
            <a href={PHONE} className="rb-text-link">
              <Phone size={18} /> (813) 694-8671
            </a>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
export function Footer() {
  const { phone, email, social, legalName, address } = SITE_IDENTITY;
  return (
    <footer className="rb-footer g-band g-band--evening">
      <div className={`rb-wrap rb-footer-top${ACQ_HUB_LINKS.length > 0 ? " rb-footer-top--hubs" : ""}`}>
        <div>
          <Link to="/" className="rb-brand rb-brand--mark">
            <FooterMark />
          </Link>
          <p>
            Every call answered.
            <br />
            Better-run businesses.
          </p>
          <div className="ip-socials">
            <a
              href={social.x}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chleb AI on X"
            >
              X
            </a>
            <a href={social.linkedin} target="_blank" rel="noopener noreferrer">
              LinkedIn
            </a>
            <a
              href={social.instagram}
              target="_blank"
              rel="noopener noreferrer"
            >
              Instagram
            </a>
          </div>
          <a href={`mailto:${email}`}>{email}</a>
          <a href={phone.tel}>{phone.display}</a>
        </div>
        <div>
          <h3>Products</h3>
          <Link to="/solutions">All solutions</Link>
          {SOLUTIONS.map((s) => (
            <a key={s.path} href={s.path}>
              {s.name}
            </a>
          ))}
          <Link to="/pricing">Plans & pricing</Link>
        </div>
        <div>
          <h3>Explore</h3>
          <Link to="/industries">Your industry</Link>
          <Link to="/#profit-engine">Revenue calculator</Link>
          <Link to="/comparisons">Compare options</Link>
          <Link to="/about">Meet the founders</Link>
          <Link to="/tampa">Chleb AI in Tampa, Florida</Link>
          <Link to="/why-us">Why Chleb</Link>
          <Link to="/faq">Questions & answers</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/aeo">Answer Engine Optimization</Link>
          <Link to="/guides/ai-search-visibility">
            AI Search Visibility Guide
          </Link>
          <Link to="/guides/ai-receptionist-cost">
            AI Receptionist Cost Guide
          </Link>
          <Link to="/contact">Contact us</Link>
        </div>
        {/*
          Compare column, 2026-09-22.

          Every /alternatives/<slug> page existed, sat in the sitemap, and had
          ZERO inbound links from the homepage. Search Console had chleb.ai at
          position 72 for "smith.ai alternatives" and 63 for "air ai
          alternatives": the pages rank at all, they just receive no internal
          authority from the strongest page on the site. Orphaned pages are the
          cheapest ranking problem there is to fix.

          Generated from COMPARISONS rather than hand-listed, so a new
          comparison page is linked the moment it is added and this column can
          never silently fall behind the data.
        */}
        <div>
          <h3>Compare</h3>
          {COMPARISONS.map((c) => (
            <Link key={c.slug} to={`/alternatives/${c.slug}`}>
              vs {c.competitor}
            </Link>
          ))}
          <Link to={HVAC_ROUNDUP_PATH}>AI receptionists for HVAC, compared</Link>
          <Link to="/comparisons">All comparisons</Link>
        </div>
        {/*
          Tools and guides, 2026-09-28: the buyer tools, demos, switching
          guides and research hubs, generated from the acquisition registry so a
          hub appears once it has a published page and never before.
        */}
        {ACQ_HUB_LINKS.length > 0 && (
          <div>
            <h3>Tools and guides</h3>
            {ACQ_HUB_LINKS.map((h) => (
              <Link key={h.path} to={h.path}>
                {h.label}
              </Link>
            ))}
          </div>
        )}
        <div className="rb-footer-note">
          <h3>Let's talk about your business.</h3>
          <p>
            Tell us what your front office needs. We’ll show you what Chleb can
            handle.
          </p>
          <Link className="g-btn g-btn--small" to="/book">
            Book a demo <ArrowUpRight size={16} />
          </Link>
          <div className="rb-preferred-source" aria-label="Google Preferred Sources">
            <div
              {...{
                "google-add-preferred-source-btn": "",
                "data-theme": "light",
                "data-lang": "en",
              }}
            />
          </div>
        </div>
      </div>
      <div className="rb-wrap rb-footer-bottom">
        <span>
          © {new Date().getFullYear()} {legalName} · {address.locality},{" "}
          {address.region}
        </span>
        <div>
          <Link to="/privacy-policy">Privacy</Link>
          <Link to="/terms-of-service">Terms</Link>
          <Link to="/cookie-policy">Cookie policy</Link>
          <Link to="/security">Security</Link>
          <Link to="/accessibility">Accessibility</Link>
          <Link to="/verifications">Trust & security</Link>
          <button onClick={openCookieSettings}>Cookie settings</button>
        </div>
      </div>
    </footer>
  );
}
function useRouteEntry() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
    else {
      const t = setTimeout(
        () => document.getElementById(hash.slice(1))?.scrollIntoView(),
        120,
      );
      return () => clearTimeout(t);
    }
  }, [pathname, hash]);
  return pathname === "/"
    ? "home"
    : pathname.split("/").filter(Boolean).join("-");
}

export function Shell({ children }: { children: ReactNode }) {
  const page = useRouteEntry();
  return (
    <div className="rebuild" data-page={page}>
      <a className="rb-skip" href="#main">
        Skip to content
      </a>
      <Header />
      <main id="main">{children}</main>
      <Footer />
      <BookingPopup />
    </div>
  );
}

/**
 * The site's chrome around a page body that is not ours to re-scope.
 *
 * /aeo and /guides/ai-search-visibility are Tailwind pages with their own
 * markup, and src/rebuild/aeo-glass.css deliberately re-grounds them on the
 * glass tokens from OUTSIDE `.rebuild`: pulling their bodies inside it would
 * hand every heading the site's own h1 and h2 recipe, which outranks the
 * Tailwind sizes those pages set for themselves, and the pages would come
 * apart. That decision stands.
 *
 * What did not survive looking at the frames is that both pages also carried
 * a second header and a second footer: different links, a different logo
 * lockup, a "Sign In" and a "Call Us" the other thirty-six routes do not have,
 * and no skip link at all, which quietly made a sentence on /accessibility
 * untrue. The chrome below is the site's, scoped so its own stylesheet
 * reaches it; the body between them keeps the scope it had.
 */
export function PageChrome({ children }: { children: ReactNode }) {
  const page = useRouteEntry();
  return (
    <>
      <div className="rebuild" data-page={page}>
        <a className="rb-skip" href="#main">
          Skip to content
        </a>
        <Header />
      </div>
      {children}
      <div className="rebuild" data-page={page}>
        <Footer />
        <BookingPopup />
      </div>
    </>
  );
}
// Several page titles are two sentences ("AI receptionist for HVAC companies.
// Never miss another after-hours emergency call."). Set whole at display size
// they run to four and eight lines of uppercase slab. Split at the first
// sentence instead: the first is the display line, the rest a sentence-case
// lead. The trailing space stays inside the first span, so the element's text
// content is byte-identical to the h1 scripts/prerender.cjs stamps.
const splitTitle = (title: ReactNode) => {
  if (typeof title !== "string") return null;
  const match = title.match(/^(.+?[.!?]\s+)(\S[\s\S]*)$/);
  return match ? ([match[1], match[2]] as const) : null;
};

export function DisplayTitle({ children }: { children: string }) {
  const parts = splitTitle(children);
  if (!parts) return <>{children}</>;
  return (
    <>
      <span className="rb-h1-display">{parts[0]}</span>
      <span className="rb-h1-lede">{parts[1]}</span>
    </>
  );
}

export function PageHeading({
  label,
  title,
  children,
}: {
  label?: string;
  title: ReactNode;
  children?: ReactNode;
}) {
  const parts = splitTitle(title);
  return (
    <section className="rb-page-heading rb-wrap">
      {label && <p className="rb-eyebrow">{label}</p>}
      <h1>
        {parts ? (
          <>
            <span className="rb-h1-display">{parts[0]}</span>
            <span className="rb-h1-lede">{parts[1]}</span>
          </>
        ) : (
          title
        )}
      </h1>
      {children && <div className="rb-lede">{children}</div>}
    </section>
  );
}
// The close returns the page to the world it opened on, graded to evening.
// One heading, one line, one button, the number. It holds: nothing here fades
// out, so the last screen of every page is a statement rather than empty air.
export function Close({
  title = "Your next missed call is the one we should talk about.",
  line = CLOSE_LINE,
}: {
  title?: string;
  line?: string;
}) {
  return (
    <section className="rb-close ch-close g-band g-band--evening">
      <div className="rb-wrap">
        <h2>{title}</h2>
        <p className="g-lede">{line}</p>
        <div className="rb-actions">
          <Link to="/book" className="g-btn">
            Book a demo <ArrowUpRight size={17} />
          </Link>
          <a className="rb-close-number" href={SITE_IDENTITY.phone.tel}>
            <Phone size={15} /> {SITE_IDENTITY.phone.display}
          </a>
        </div>
      </div>
    </section>
  );
}
